import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  fetchBusinessProfileNotes,
  selectBusinessProfileNotes,
  selectBusinessProfileStatus,
  clearBusinessProfileNotes,
} from '../../slices/business-profile/businessProfileSlice';
import styles from './NotesTab.module.scss';
import NotesCard from './NotesCard';
import CreateNoteModal from '../create-note-modal/CreateNoteModal';
import {
  fetchEmployeeNotes,
  selectEmployeeNotes,
  clearEmployeeNotes,
} from '../../slices/employees/employeesSlice';
import {
  clearCustomerNotes,
  fetchCustomerNotes,
  selectCustomerNotes,
} from '../../slices/customers/customersSlice';
import {
  clearSupplierNotes,
  fetchSupplierNotes,
  selectSupplierNotes,
} from '../../slices/suppliers/suppliersSlice';
import Button from '@mui/material/Button';
import { RootState } from '../../store';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { USER_PERMISSIONS } from '../constants/permissions';

interface NotesTabsProps {
  companyId?: number;
  employee?: any;
  customer?: any;
  supplier?: any;
  key: string;
}

const NotesTab: React.FC<NotesTabsProps> = ({ employee, companyId, customer, supplier }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const businessProfileNotes = useAppSelector(selectBusinessProfileNotes);
  const employeeNotes = useAppSelector(selectEmployeeNotes);
  const customerNotes = useAppSelector(selectCustomerNotes);
  const supplierNotes = useAppSelector(selectSupplierNotes);
  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);
  const employeesStatus = useAppSelector((state: RootState) => state.employees.status);
  const customersStatus = useAppSelector((state: RootState) => state.customers.status);
  const suppliersStatus = useAppSelector((state: RootState) => state.suppliers.status);
  const isLoading =
    businessProfileStatus === 'loading' ||
    employeesStatus === 'loading' ||
    customersStatus === 'loading' ||
    suppliersStatus === 'loading';
  const modalRef = useRef<HTMLDivElement>(null);
  const checkPermission = useCheckPermission();
  const checkCreateNotePermission = () => {
    if (supplier?.id) return checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_CREATE_NOTE);
    if (customer?.id) return checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_CREATE_NOTE);
    if (employee?.id) return checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_CREATE_NOTE);
    return checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_CREATE_NOTE);
  };

  useEffect(() => {
    if (companyId !== undefined) {
      dispatch(fetchBusinessProfileNotes({ companyId }));
    }
    return () => {
      dispatch(clearBusinessProfileNotes()); // Clear business profile notes on unmount
    };
  }, [dispatch, companyId]);

  useEffect(() => {
    if (employee?.id !== undefined) {
      dispatch(fetchEmployeeNotes(employee.id));
    }
    return () => {
      dispatch(clearEmployeeNotes()); // Clear employee notes on unmount
    };
  }, [dispatch, employee]);

  useEffect(() => {
    if (customer?.id !== undefined) {
      dispatch(fetchCustomerNotes(customer.id));
    }
    return () => {
      dispatch(clearCustomerNotes()); // Clear customer notes on unmount
    };
  }, [dispatch, customer]);

  useEffect(() => {
    if (supplier?.id !== undefined) {
      dispatch(fetchSupplierNotes(supplier.id));
    }
    return () => {
      dispatch(clearSupplierNotes()); // Clear supplier notes on unmount
    };
  }, [dispatch, supplier]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const detectNotesArray = () => {
    if (supplier?.id) {
      return supplierNotes;
    } else if (customer?.id) {
      return customerNotes;
    } else if (employee?.id) {
      return employeeNotes;
    } else {
      return businessProfileNotes;
    }
  };

  const detectNoteCardType = (note: any) => {
    if (supplier?.id) {
      return <NotesCard supnote={note} />;
    } else if (customer?.id) {
      return <NotesCard cusnote={note} />;
    } else if (employee?.id) {
      return <NotesCard empnote={note} />;
    } else {
      return <NotesCard note={note} />;
    }
  };

  const toogleShowAddlModal = () => {
    setIsModalOpen((prev) => !prev);
  };
  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <Typography sx={{ marginLeft: '0.5rem', marginBottom: '0.5rem' }} variant='h6'>
          {t('businessProfile.notesTab')}
        </Typography>
        <Grid container alignItems='center'>
          <Grid item mb={'16px'}>
            <Tooltip
              title={
                checkCreateNotePermission()
                  ? t('buttons.createNote')
                  : t('messages.contactAdministrator')
              }
              arrow
              placement='top'
            >
              <Button
                className='btn-action-basic'
                disabled={!checkCreateNotePermission()}
                onClick={toogleShowAddlModal}
              >
                {t('buttons.createNote')}
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
        {/* <Card>
          <CardContent className={isLoading ? styles.card_content_min_height : ''}> */}
        <Grid container>
          {isLoading && detectNotesArray().length === 0 && (
            <ProgressOverlay className='position-relative bg-transparent' />
          )}
          {[...detectNotesArray()].map((note) => (
            <Grid className={styles.card_border_bottom} size={{ xs: 12 }} key={note.id}>
              {detectNoteCardType(note)}
              {/* <NoteItem text={note.content} date={note.date} name={note.author} /> */}
            </Grid>
          ))}
        </Grid>
      </Grid>
      {isModalOpen && (
        <Box ref={modalRef}>
          <CreateNoteModal
            companyId={companyId}
            employeeId={employee?.id}
            customerId={customer?.id}
            supplierId={supplier?.id}
            open={isModalOpen}
            onClose={() => {
              toogleShowAddlModal();
            }}
          />
        </Box>
      )}
    </Grid>
  );
};

export default NotesTab;
